import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'
import * as api from '../../api/client'
import { describeError } from '../../api/errors'
import { reportRequestFailure } from '../../api/serverStatus'
import type { Take } from '../../api/types'
import { getRecorderLag } from '../../audio/calibration'
import { isRecordingSupported } from '../../audio/mime'
import { LineRecording, RecordingCancelled, requestMicStream, stopStream } from '../../audio/recorder'
import type { RecordedTake, RecordingPhase } from '../../audio/recorder'
import { FragmentPlayer, clipRange } from '../../game/clipPlayer'
import { countRecorded, firstUnrecorded } from '../../game/order'
import type { PlayItem } from '../../game/order'
import { formatTime } from '../../lib/format'
import { acknowledgeHeadphones, headphonesAcknowledged } from '../../lib/prefs'
import { pushToast } from '../../lib/toast'
import { Modal } from '../Modal'
import { PlayHandoff } from './PlayHandoff'

/** Состояния экрана реплики. В каждом ровно одно очевидное действие. */
type Stage = 'handoff' | 'idle' | 'listening' | 'recording' | 'review'

export type PlayRunProps = {
  /** Плейлист фиксируется на входе в забег: индексы не должны разъезжаться. */
  items: PlayItem[]
  videoSrc: string
  recordedIds: ReadonlySet<string>
  listenOriginal: boolean
  /**
   * Проигрывать ли дубль сразу после записи (docs/GAME.md, «Цикл записи»).
   * Выключено — дубль всё равно готов к прослушиванию, но только по кнопке.
   */
  autoPlayTake: boolean
  onRecorded: (take: Take | null) => void
  /**
   * Куда отправлять записанное. По умолчанию — дубль проекта; сетевая игра
   * подменяет загрузчик на сессионный, всё остальное поведение общее.
   */
  uploadTake?: (lineId: string, result: RecordedTake) => Promise<Take | null>
  onExit: () => void
  onFinish: () => void
}

export function PlayRun({
  items,
  videoSrc,
  recordedIds,
  listenOriginal,
  autoPlayTake,
  onRecorded,
  uploadTake: uploadOverride,
  onExit,
  onFinish,
}: PlayRunProps): JSX.Element {
  // Восстановление после перезагрузки: начинаем с первой реплики без дубля.
  const [index, setIndex] = useState(() => firstUnrecorded(items, recordedIds))
  const [stage, setStage] = useState<Stage>('idle')
  const [phase, setPhase] = useState<RecordingPhase>('idle')
  const [countdown, setCountdown] = useState(0)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [videoReady, setVideoReady] = useState(false)
  const [videoFailed, setVideoFailed] = useState(false)
  const [askHeadphones, setAskHeadphones] = useState(false)
  const [reviewFallback, setReviewFallback] = useState(false)

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const playerRef = useRef<FragmentPlayer | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const sessionRef = useRef<LineRecording | null>(null)
  const mountedRef = useRef(true)
  /**
   * Каждое действие игрока обесценивает предыдущий асинхронный сценарий:
   * пока декодировался дубль, игрок мог уже нажать «Ещё раз».
   */
  const opRef = useRef(0)

  const item: PlayItem | undefined = items[index]
  const supported = isRecordingSupported()

  const nextOp = (): number => {
    opRef.current += 1
    return opRef.current
  }

  // ------------------------------------------------------------ ресурсы

  const ensureStream = async (): Promise<MediaStream> => {
    const existing = streamRef.current
    if (existing && existing.getAudioTracks().some((track) => track.readyState === 'live')) {
      return existing
    }
    stopStream(existing)
    const stream = await requestMicStream()
    streamRef.current = stream
    return stream
  }

  const uploadTake = async (lineId: string, result: RecordedTake): Promise<void> => {
    setUploadProgress(0)
    if (uploadOverride) {
      try {
        const take = await uploadOverride(lineId, result)
        if (mountedRef.current) onRecorded(take)
      } catch (err) {
        reportRequestFailure(err)
        pushToast('error', `Дубль не сохранился: ${describeError(err)}. Нажмите R, чтобы переписать.`)
      } finally {
        if (mountedRef.current) setUploadProgress(null)
      }
      return
    }
    // Отправка идёт параллельно с прослушиванием: ждать сеть в главный момент
    // игры нельзя. Дубль уже записан, потеряться ему негде.
    const handle = api.uploadTake(
      lineId,
      {
        blob: result.blob,
        filename: result.filename,
        duration: result.duration,
        offset: result.offset,
        author: 'local',
      },
      (fraction) => {
        if (mountedRef.current) setUploadProgress(fraction)
      },
    )
    try {
      const take = await handle.promise
      if (mountedRef.current) onRecorded(take)
    } catch (err) {
      reportRequestFailure(err)
      pushToast('error', `Дубль не сохранился: ${describeError(err)}. Нажмите R, чтобы переписать.`)
    } finally {
      if (mountedRef.current) setUploadProgress(null)
    }
  }

  // ------------------------------------------------------------ сценарии

  const listenNow = async (): Promise<void> => {
    const player = playerRef.current
    if (!player || !item) return
    const op = nextOp()
    player.clearTake()
    setReviewFallback(false)
    setStage('listening')
    const range = clipRange(item.line.start, item.line.end)
    try {
      await player.play({ from: range.from, to: range.to, withOriginal: true, withTake: false })
    } catch (err) {
      pushToast('info', describeError(err))
    }
    if (!mountedRef.current || opRef.current !== op) return
    setStage('idle')
  }

  /** Реплика готова к работе: либо сразу слушаем оригинал, либо ждём пробел. */
  const ready = (): void => {
    if (!item) return
    setStage('idle')
    if (listenOriginal) {
      void listenNow()
      return
    }
    nextOp()
    void playerRef.current?.showFrame(item.line.start)
  }

  const enterLine = (): void => {
    nextOp()
    const player = playerRef.current
    player?.stop()
    player?.clearTake()
    setPhase('idle')
    setCountdown(0)
    setUploadProgress(null)
    setReviewFallback(false)
    if (!item) {
      onFinish()
      return
    }
    if (item.handoff) {
      setStage('handoff')
      void player?.showFrame(item.line.start)
      return
    }
    ready()
  }

  const review = async (result: RecordedTake, op: number): Promise<void> => {
    if (!item) return
    setStage('review')
    setPhase('idle')
    const player = playerRef.current
    if (!player) return
    // Абсолютный таймкод начала файла: line.start + take.offset (docs/API.md).
    const ok = await player.loadTake(result.blob, item.line.start + result.offset)
    if (!mountedRef.current || opRef.current !== op) return
    setReviewFallback(!ok)
    // Галочка выключена — экран выбора уже показан, дубль декодирован
    // и ждёт кнопки «Прослушать». Ничего не играем.
    if (!autoPlayTake) return
    const range = clipRange(item.line.start, item.line.end)
    try {
      await player.play({ from: range.from, to: range.to, withOriginal: !ok, withTake: ok })
    } catch (err) {
      pushToast('info', describeError(err))
    }
  }

  const startRecording = async (): Promise<void> => {
    const video = videoRef.current
    if (!item || !video) return
    if (!videoReady || videoFailed) {
      pushToast('info', 'Видео ещё не готово — секунду.')
      return
    }
    if (!headphonesAcknowledged()) {
      setAskHeadphones(true)
      return
    }
    const op = nextOp()
    const player = playerRef.current
    player?.stop()
    player?.clearTake()
    setReviewFallback(false)

    let stream: MediaStream
    try {
      stream = await ensureStream()
    } catch (err) {
      pushToast('error', describeError(err))
      return
    }
    if (!mountedRef.current || opRef.current !== op) return

    // Оригинал звучит в наушниках во время записи — так проще попасть в темп.
    video.muted = false
    setStage('recording')
    setPhase('seeking')
    setCountdown(0)

    const session = new LineRecording({
      video,
      stream,
      lineStart: item.line.start,
      lineEnd: item.line.end,
      recorderLag: getRecorderLag(),
      onProgress: (progress) => {
        if (!mountedRef.current) return
        setPhase(progress.phase)
        setCountdown(progress.countdown)
      },
    })
    sessionRef.current = session

    try {
      const result = await session.run()
      sessionRef.current = null
      if (!mountedRef.current || opRef.current !== op) return
      void uploadTake(item.line.id, result)
      // С включённой галочкой — сразу автовоспроизведение, ради этого момента
      // и играют. С выключенной review() только готовит дубль к прослушиванию.
      await review(result, op)
    } catch (err) {
      sessionRef.current = null
      if (!(err instanceof RecordingCancelled)) pushToast('error', describeError(err))
      if (mountedRef.current && opRef.current === op) {
        setPhase('idle')
        setStage('idle')
      }
    }
  }

  const replay = (): void => {
    const player = playerRef.current
    if (!player || !item) return
    nextOp()
    const range = clipRange(item.line.start, item.line.end)
    const withTake = player.hasTake()
    void player
      .play({ from: range.from, to: range.to, withOriginal: !withTake, withTake })
      .catch((err: unknown) => pushToast('info', describeError(err)))
  }

  const advance = (): void => {
    nextOp()
    playerRef.current?.stop()
    if (index + 1 >= items.length) {
      onFinish()
      return
    }
    setIndex(index + 1)
  }

  const stopEarly = (): void => {
    sessionRef.current?.finish()
  }

  const stopListening = (): void => {
    nextOp()
    playerRef.current?.stop()
    setStage('idle')
  }

  // ---------------------------------------------------------- клавиатура

  const onSpace = (): void => {
    if (stage === 'handoff') {
      ready()
      return
    }
    if (stage === 'recording') {
      stopEarly()
      return
    }
    if (stage === 'review') {
      advance()
      return
    }
    void startRecording()
  }

  const onEnter = (): void => {
    if (stage === 'handoff') {
      ready()
      return
    }
    if (stage === 'review') {
      advance()
      return
    }
    if (stage === 'idle' || stage === 'listening') void startRecording()
  }

  const onKey = (e: KeyboardEvent): void => {
    if (askHeadphones || e.repeat) return
    if (e.altKey || e.ctrlKey || e.metaKey) return
    const target = e.target as HTMLElement | null
    const tag = target?.tagName.toLowerCase()
    if (tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable) {
      return
    }
    switch (e.code) {
      case 'Space':
        e.preventDefault()
        onSpace()
        break
      case 'Enter':
      case 'NumpadEnter':
        e.preventDefault()
        onEnter()
        break
      case 'KeyR':
        e.preventDefault()
        if (stage !== 'recording') void startRecording()
        break
      case 'ArrowRight':
        e.preventDefault()
        if (stage !== 'recording') advance()
        break
      case 'Escape':
        e.preventDefault()
        onExit()
        break
      default:
        break
    }
  }

  // Обработчики пересобираются каждый рендер, слушатель вешается один раз:
  // иначе в игровом цикле накапливались бы подписки на window.
  const keyRef = useRef(onKey)
  keyRef.current = onKey
  const enterRef = useRef(enterLine)
  enterRef.current = enterLine

  useEffect(() => {
    mountedRef.current = true
    const video = videoRef.current
    if (video) {
      video.muted = true
      playerRef.current = new FragmentPlayer(video)
    }
    return () => {
      mountedRef.current = false
      opRef.current += 1
      sessionRef.current?.cancel()
      sessionRef.current = null
      playerRef.current?.dispose()
      playerRef.current = null
      stopStream(streamRef.current)
      streamRef.current = null
    }
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => keyRef.current(e)
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // Перемотка до loadedmetadata молча не срабатывает — ждём готовности видео.
  useEffect(() => {
    if (!videoReady) return
    enterRef.current()
  }, [index, videoReady])

  // ------------------------------------------------------------- вывод

  const total = items.length
  const done = countRecorded(items, recordedIds)
  const percent = total > 0 ? Math.round((done / total) * 100) : 0
  const recording = stage === 'recording'

  if (!supported) {
    return (
      <div className="play-page">
        <div className="empty">
          <p>Этот браузер не поддерживает запись звука (MediaRecorder / getUserMedia).</p>
          <p className="faint">
            Откройте приложение в Chrome, Edge, Firefox или Safari — по HTTPS либо на localhost.
          </p>
          <button type="button" onClick={onExit}>
            Выйти в редактор
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="play-screen">
      <div className="play-top">
        <button type="button" className="ghost" onClick={onExit} title="Esc">
          ✕ Выйти
        </button>
        <span className="spacer" />
        <span className="mono play-counter">
          {Math.min(index + 1, total)} / {total}
        </span>
      </div>
      <div className="progress play-progress">
        <i style={{ width: `${percent}%` }} />
      </div>

      <div className="play-stage">
        <div className="play-role" style={{ color: item ? item.roleColor : 'var(--text-dim)' }}>
          {item ? item.roleName : '—'}
        </div>
        <div className={`play-text${item && item.line.text ? '' : ' placeholder'}`}>
          {item ? item.line.text || 'Текст реплики не задан — сыграйте по картинке' : ''}
        </div>
        {item && item.line.textOriginal && item.line.textOriginal.trim() ? (
          <div className="play-original" title="Оригинальная реплика">
            {item.line.textOriginal}
          </div>
        ) : null}

        <div className="play-video">
          <video
            ref={videoRef}
            src={videoSrc}
            preload="auto"
            playsInline
            onLoadedMetadata={() => {
              setVideoFailed(false)
              setVideoReady(true)
            }}
            onError={() => setVideoFailed(true)}
          />
        </div>

        <div className={`play-status${recording && phase === 'recording' ? ' active' : ''}`}>
          {videoFailed ? (
            <span className="muted">
              Видео не загрузилось. Проверьте, что сервер на связи, и обновите страницу — записанное
              не потеряется.
            </span>
          ) : !videoReady ? (
            <span className="faint">Загружаю видео…</span>
          ) : null}
          {!videoFailed && videoReady && stage === 'idle' ? (
            <span className="faint">
              {item ? `${formatTime(item.line.start)} → ${formatTime(item.line.end)}` : ''} · пробел
              — запись, отсчёт и автостоп сами
            </span>
          ) : null}
          {stage === 'listening' ? <span className="muted">♪ Слушаем оригинал…</span> : null}
          {recording && phase === 'seeking' ? <span className="faint">Отматываю…</span> : null}
          {recording && phase === 'preroll' ? (
            <>
              <div className="countdown">{Math.max(0, countdown).toFixed(1)}</div>
              <span className="faint">приготовьтесь</span>
            </>
          ) : null}
          {recording && phase === 'recording' ? (
            <div className="row">
              <span className="rec-dot" />
              <strong>Говорите</strong>
            </div>
          ) : null}
          {recording && (phase === 'finishing' || phase === 'idle') ? (
            <span className="faint">Завершаю запись…</span>
          ) : null}
          {stage === 'review' ? (
            <span className="muted">
              {reviewFallback
                ? 'Браузер не смог проиграть дубль — но на сервере он есть'
                : autoPlayTake
                  ? '▶ Вот что получилось'
                  : 'Дубль записан — «Прослушать», если хотите проверить'}
            </span>
          ) : null}
        </div>

        <div className="play-actions">
          {stage === 'review' ? (
            <>
              <button type="button" className="primary play-main" onClick={advance}>
                Дальше →
              </button>
              <button type="button" onClick={() => void startRecording()}>
                ⟲ Ещё раз <span className="faint">R</span>
              </button>
              <button
                type="button"
                className={autoPlayTake ? 'ghost' : ''}
                onClick={replay}
              >
                {autoPlayTake ? '▶ Прослушать снова' : '▶ Прослушать'}
              </button>
              <button type="button" className="ghost" onClick={advance}>
                Пропустить →
              </button>
            </>
          ) : recording ? (
            <>
              <button type="button" className="danger play-main" onClick={stopEarly}>
                ■ Стоп <span className="faint">пробел</span>
              </button>
              {/* Отмену не помечаем новой операцией: сценарий записи должен
                  сам поймать RecordingCancelled и вернуть экран в idle. */}
              <button type="button" className="ghost" onClick={() => sessionRef.current?.cancel()}>
                Отменить дубль
              </button>
            </>
          ) : stage === 'handoff' ? null : (
            <>
              <button
                type="button"
                className="record play-main"
                disabled={!videoReady || videoFailed}
                onClick={() => void startRecording()}
              >
                ● Записать <span className="faint">пробел</span>
              </button>
              {stage === 'listening' ? (
                <button type="button" className="ghost" onClick={stopListening}>
                  ■ Хватит слушать
                </button>
              ) : (
                <button
                  type="button"
                  className="ghost"
                  disabled={!videoReady || videoFailed}
                  onClick={replay}
                >
                  ▶ Послушать оригинал
                </button>
              )}
              <button type="button" className="ghost" onClick={advance} title="→">
                Пропустить →
              </button>
            </>
          )}
        </div>

        {uploadProgress !== null ? (
          <div className="play-upload">
            <span className="faint">сохраняю дубль</span>
            <div className="progress" style={{ flex: 1 }}>
              <i style={{ width: `${Math.round(uploadProgress * 100)}%` }} />
            </div>
          </div>
        ) : null}
      </div>

      <div className="play-hints faint">
        <span>
          <b>пробел</b> запись / стоп
        </span>
        <span>
          <b>enter</b> принять
        </span>
        <span>
          <b>R</b> переписать
        </span>
        <span>
          <b>→</b> пропустить
        </span>
        <span>
          <b>esc</b> выход
        </span>
      </div>

      {stage === 'handoff' && item ? (
        <PlayHandoff
          roleName={item.roleName}
          roleColor={item.roleColor}
          count={item.segmentSize}
          onReady={ready}
        />
      ) : null}

      {askHeadphones ? (
        <Modal
          title="Наденьте наушники"
          onClose={() => setAskHeadphones(false)}
          footer={
            <>
              <button type="button" className="ghost" onClick={() => setAskHeadphones(false)}>
                Отмена
              </button>
              <span className="spacer" />
              <button
                type="button"
                className="primary"
                onClick={() => {
                  acknowledgeHeadphones()
                  setAskHeadphones(false)
                  void startRecording()
                }}
              >
                Наушники надеты, пишем
              </button>
            </>
          }
        >
          <p>
            Во время записи звучит фрагмент видео. Без наушников оригинальная дорожка попадёт
            в микрофон, и убрать её из дубля уже не выйдет.
          </p>
          <p className="faint">
            Спросим один раз — дальше игра больше не будет прерываться на это.
          </p>
        </Modal>
      ) : null}
    </div>
  )
}
